import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import AdSense from '../components/AdSense';
import HeroChartBg from '../components/HeroChartBg';

const TOOLS = [
  {
    name: 'Option Analysis',
    tagline: 'Day level greek component chart for price option changes',
    description: 'Enter any strike and expiry to see the full theoretical breakdown — Delta, Gamma, Theta, Vega, Rho — with a waterfall chart showing how each Greek contributes to the option price.',
    status: 'live',
    to: '/options-breakdown',
  },
  {
    name: 'IV Calculator',
    tagline: 'IV Rank & Percentile for 53 F&O symbols',
    description: 'Daily implied volatility, IV Rank (52-week), and IV Percentile (90-day) for all NIFTY 50 stocks and the three major indices. Updated every trading day at 6:30 PM IST.',
    status: 'live',
    to: '/iv-calculator',
  },
  {
    name: 'Max Pain Analysis',
    tagline: 'Options max pain level for indices & stocks',
    description: 'Calculates the price at which option buyers lose the most collectively — often used as a gravitational price target near expiry. Currently in development.',
    status: 'wip',
    to: null,
  },
];

function StatusDot({ status }) {
  if (status === 'live') {
    return (
      <span className="relative flex items-center justify-center w-3 h-3 shrink-0">
        <span className="absolute inline-flex w-full h-full rounded-full bg-green-400 opacity-60 animate-ping" />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-green-500" />
      </span>
    );
  }
  return <span className="w-2.5 h-2.5 rounded-full bg-orange-400 shrink-0" />;
}

function ToolCell({ tool }) {
  return (
    <Link
      to={tool.to || '#'}
      onClick={tool.to ? undefined : (e) => e.preventDefault()}
      className="group relative flex items-center justify-between gap-3 px-4 py-3 bg-surface rounded-clay shadow-clay dark:shadow-clay-dark hover:shadow-clay-hover dark:hover:shadow-clay-dark-hover transition-all duration-150 cursor-default"
      style={tool.to ? { cursor: 'pointer' } : {}}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{tool.name}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{tool.tagline}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-xs text-neutral-400 dark:text-neutral-500">
          {tool.status === 'live' ? 'Live' : 'WIP'}
        </span>
        <StatusDot status={tool.status} />
      </div>
      <div className="pointer-events-none absolute bottom-full left-0 mb-2 w-full min-w-[220px] bg-surface rounded-clay shadow-clay-hover dark:shadow-none px-3 py-2.5 text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-opacity duration-150 z-20">
        {tool.description}
      </div>
    </Link>
  );
}

function ToolsGrid() {
  return (
    <div className="bg-surface rounded-clay shadow-clay dark:shadow-clay-dark p-6">
      <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-5">Tools</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {TOOLS.map((tool) => (
          <ToolCell key={tool.name} tool={tool} />
        ))}
        <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-clay text-neutral-400 dark:text-neutral-500 text-sm">
          <span className="text-lg leading-none">+</span>
          <span>More on the way</span>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  return (
    <div className="relative flex items-center justify-center py-24 px-4 font-ronzino overflow-hidden">
      <HeroChartBg />
      <div className="relative z-10 text-center max-w-2xl">
        <p className="text-xs font-normal tracking-widest uppercase text-primary-600 dark:text-primary-400 mb-4">
          OptionView
        </p>
        <h1 className="text-4xl sm:text-5xl text-neutral-900 dark:text-neutral-50 leading-tight mb-5 font-ronzino font-extrabold">
          Tools for a deeper understanding.
        </h1>
        <p className="text-neutral-600 dark:text-neutral-400 text-base font-normal leading-relaxed">
          OptionView aims to be a set of specific, bespoke tools that allows in-depth and actionable analysis of options.
        </p>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function Home() {
  return (
    <>
      <SEOHead
        canonicalPath="/"
        title="OptionView — Free Options Analysis Tools for Indian Traders"
        description="Free options analysis tools for Indian F&O traders. IV Calculator with IV Rank and Percentile for 53 NSE symbols. Black-Scholes Greeks, PDE decomposition and waterfall charts. Updated daily from NSE Bhavcopy."
        keywords="IV calculator India, implied volatility calculator, NIFTY IV, Bank Nifty IV, Fin Nifty IV, options trading India, IV rank, IV percentile, options greeks, Black-Scholes calculator, delta gamma theta vega, NSE F&O, NIFTY50 options, options breakdown"
        image="/og-image.png"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-5">

        {/* ── Row 1: Hero ─────────────────────────────────────────────────── */}
        <HeroSection />

        {/* ── Row 2: Tools status grid ─────────────────────────────────────── */}
        <ToolsGrid />

        {/* ── About ────────────────────────────────────────────────────────── */}
        <div className="bg-surface rounded-clay shadow-clay dark:shadow-clay-dark p-6">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-3">About</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed mb-2">
            OptionView is a toolkit for Options traders. The goal is simple, help traders understand options better, and attribute price moves correctly.
            This is a POC, and I'm looking forward to your feedback on additional tools can be offered.
            Please note, this tool currently works on free and publically available data, and for a few scrips, for now.
          </p>
          <Link
            to="/about"
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
          >
            More about this project →
          </Link>
        </div>

        {/* ── Ad ──────────────────────────────────────────────────────────── */}
        <AdSense adSlot="" label="Home Banner" adStyle={{ minHeight: 90 }} />

        {/* ── Row 4: Blog feature ──────────────────────────────────────────── */}
        <div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
            I also write to help understand concepts deeper.
          </p>
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              From the blog
            </h2>
            <Link to="/blog" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
              All articles →
            </Link>
          </div>

          <Link
            to="/blog/black-scholes/what-is-black-scholes"
            className="group block bg-surface rounded-clay shadow-clay dark:shadow-clay-dark hover:shadow-clay-hover dark:hover:shadow-clay-dark-hover transition-all duration-200 p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-2 py-0.5 rounded-full">
                Black-Scholes
              </span>
              <span className="text-xs text-neutral-400">8 min read</span>
            </div>
            <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors mb-2">
              Understanding the Black-Scholes PDE
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed line-clamp-2">
              A plain-English explanation of the Black-Scholes partial differential equation — what each term means, how it connects to option prices, and why the Greeks emerge naturally from it.
            </p>
            <div className="mt-3 text-sm font-medium text-primary-600 dark:text-primary-400 inline-flex items-center gap-1">
              Read article
              <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        </div>

        {/* ── Row 5: FAQ ───────────────────────────────────────────────────── */}
        <div>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
            FAQ
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Have questions?{' '}
            <Link to="/faq" className="text-primary-600 dark:text-primary-400 hover:underline">
              Read the full FAQ →
            </Link>
          </p>
        </div>

      </div>
    </>
  );
}
